import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Droplet, Loader2, Trash2, Sparkles, Thermometer, FlaskConical,
    AlertTriangle, CheckCircle2, Users, FileDown,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LangContext";
import {
    logWater, fetchWaterHistory, deleteWater, fetchJoinedClasses,
    myWaterCsvUrl,
} from "@/lib/api";

const FIELDS = [
    { key: "ph", label: "pH", min: 0, max: 14, step: 0.1, color: "var(--lime)", ideal: [6.8, 7.2] },
    { key: "ammonia", label: "NH₃", min: 0, max: 8, step: 0.1, color: "var(--coral)", ideal: [0, 0.25] },
    { key: "nitrite", label: "NO₂", min: 0, max: 5, step: 0.1, color: "var(--cyan)", ideal: [0, 0.25] },
    { key: "nitrate", label: "NO₃", min: 0, max: 200, step: 1, color: "var(--lime)", ideal: [5, 80] },
    { key: "temp_c", label: "°C", min: 0, max: 40, step: 0.5, color: "var(--cyan)", ideal: [20, 28] },
];

const T = {
    en: {
        tag: "// water lab · AI coached",
        h1a: "Log a reading.",
        h1b: "FLOW reads it back.",
        blurb: "Drop your pH, ammonia, nitrite, nitrate and tank temp. We'll tell you if you're golden or about to crash.",
        submit: "Log + Diagnose",
        logging: "Reading the tank…",
        notes: "Notes",
        notesPh: "anything weird? fish behavior, smell, recent feeding…",
        history: "Recent Readings",
        none: "No readings yet — log your first.",
        flagsLabel: "Watch outs:",
        deleteConfirm: "Delete this reading?",
        ph_low: "pH too low",
        ph_high: "pH too high",
        ammonia_high: "Ammonia spike",
        nitrite_high: "Nitrite spike",
        nitrate_high: "Nitrate maxed",
        chart: "pH trend",
    },
    es: {
        tag: "// lab de agua · IA asistida",
        h1a: "Registra lectura.",
        h1b: "FLOW te responde.",
        blurb: "Anota pH, amoniaco, nitrito, nitrato y temperatura. Te decimos si estás bien o por colapsar.",
        submit: "Registrar + Diagnosticar",
        logging: "Leyendo el tanque…",
        notes: "Notas",
        notesPh: "¿algo raro? comportamiento, olor, alimentación reciente…",
        history: "Lecturas Recientes",
        none: "Sin lecturas — registra la primera.",
        flagsLabel: "Atención:",
        deleteConfirm: "¿Borrar esta lectura?",
        ph_low: "pH muy bajo",
        ph_high: "pH muy alto",
        ammonia_high: "Pico de amoniaco",
        nitrite_high: "Pico de nitrito",
        nitrate_high: "Nitrato saturado",
        chart: "Tendencia pH",
    },
};

export default function WaterLab() {
    const { lang } = useLang();
    const { user, loading: authLoading } = useAuth();
    const nav = useNavigate();
    const t = T[lang] || T.en;
    const [values, setValues] = useState({});
    const [notes, setNotes] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [readings, setReadings] = useState([]);
    const [classes, setClasses] = useState([]);
    const [classId, setClassId] = useState("");
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        if (authLoading) return;
        if (!user) {
            nav("/login", { replace: true });
            return;
        }
        Promise.all([fetchWaterHistory(), fetchJoinedClasses()])
            .then(([r, c]) => {
                setReadings(r);
                setClasses(c);
                setLoaded(true);
            });
    }, [user, authLoading, nav]);

    const submit = async (e) => {
        e.preventDefault();
        const payload = { lang };
        let hasAny = false;
        FIELDS.forEach((f) => {
            if (values[f.key] !== undefined && values[f.key] !== "") {
                payload[f.key] = Number(values[f.key]);
                hasAny = true;
            }
        });
        if (!hasAny) {
            alert(lang === "es" ? "Llena al menos un valor." : "Enter at least one value.");
            return;
        }
        if (notes.trim()) payload.notes = notes.trim();
        setSubmitting(true);
        try {
            const fresh = await logWater(payload);
            setReadings((rs) => [fresh, ...rs]);
            setValues({});
            setNotes("");
        } catch (err) {
            alert("Failed — try again.");
        } finally {
            setSubmitting(false);
        }
    };

    const remove = async (id) => {
        if (!window.confirm(t.deleteConfirm)) return;
        await deleteWater(id);
        setReadings((rs) => rs.filter((r) => r.id !== id));
    };

    const phSeries = useMemo(
        () =>
            readings
                .filter((r) => r.ph !== null && r.ph !== undefined)
                .slice(0, 20)
                .reverse()
                .map((r) => ({ x: r.created_at, y: r.ph })),
        [readings],
    );

    return (
        <main
            className="max-w-6xl mx-auto px-5 md:px-8 pt-10 md:pt-16 pb-24 relative"
            data-testid="water-lab-page"
        >
            <div className="glow glow-cyan -top-20 right-0" />
            <div className="relative z-10">
                <span className="tag">{t.tag}</span>
                <h1 className="font-display font-black text-white text-4xl md:text-6xl mt-3 uppercase leading-[0.95]">
                    {t.h1a}
                    <br />
                    <span className="font-brush text-[var(--cyan)] inline-block -rotate-2">
                        {t.h1b}
                    </span>
                </h1>
                <p className="font-mono text-[var(--muted)] mt-4 max-w-2xl">{t.blurb}</p>

                <div className="grid grid-cols-12 gap-6 mt-10">
                    {/* Form */}
                    <form
                        onSubmit={submit}
                        className="col-span-12 md:col-span-5 terminal p-6"
                        data-testid="water-form"
                    >
                        <div className="terminal-header -mx-6 -mt-6 px-6 py-3 flex items-center gap-2 mb-5">
                            <span className="w-2 h-2 rounded-full bg-[var(--cyan)] animate-pulse" />
                            <span className="font-mono text-xs uppercase tracking-[0.2em] text-[var(--cyan)]">
                                READING // INPUT
                            </span>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            {FIELDS.map((f) => (
                                <label key={f.key} className="block">
                                    <span
                                        className="font-mono text-[10px] uppercase tracking-[0.22em]"
                                        style={{ color: f.color }}
                                    >
                                        {f.label}
                                    </span>
                                    <div className="font-mono text-[9px] text-[var(--muted)] mt-0.5">
                                        ideal {f.ideal[0]}–{f.ideal[1]}
                                    </div>
                                    <input
                                        type="number"
                                        min={f.min}
                                        max={f.max}
                                        step={f.step}
                                        value={values[f.key] ?? ""}
                                        onChange={(e) =>
                                            setValues((v) => ({ ...v, [f.key]: e.target.value }))
                                        }
                                        className="input-line mt-1"
                                        data-testid={`water-input-${f.key}`}
                                        placeholder="—"
                                    />
                                </label>
                            ))}
                        </div>
                        <label className="block mt-5">
                            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--muted)]">
                                {t.notes}
                            </span>
                            <textarea
                                rows={2}
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                placeholder={t.notesPh}
                                className="input-line mt-1 resize-none"
                                data-testid="water-notes"
                                maxLength={300}
                            />
                        </label>
                        {classes.length > 0 && (
                            <label className="block mt-5">
                                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--coral)] flex items-center gap-1">
                                    <Users size={11} />
                                    {lang === "es"
                                        ? "Compartir con clase (opcional)"
                                        : "Share with class (optional)"}
                                </span>
                                <select
                                    value={classId}
                                    onChange={(e) => setClassId(e.target.value)}
                                    className="input-line mt-1"
                                    data-testid="water-class-select"
                                >
                                    <option value="" className="bg-black">
                                        {lang === "es" ? "— privado —" : "— private —"}
                                    </option>
                                    {classes.map((c) => (
                                        <option
                                            key={c.id}
                                            value={c.id}
                                            className="bg-black"
                                        >
                                            {c.name} · {c.code}
                                        </option>
                                    ))}
                                </select>
                            </label>
                        )}
                        <button
                            type="submit"
                            disabled={submitting}
                            className="btn-prime w-full justify-center mt-5"
                            data-testid="water-submit"
                        >
                            {submitting ? (
                                <>
                                    <Loader2 size={14} className="animate-spin" />
                                    {t.logging}
                                </>
                            ) : (
                                <>
                                    <Sparkles size={14} />
                                    {t.submit}
                                </>
                            )}
                        </button>
                    </form>

                    {/* History */}
                    <section
                        className="col-span-12 md:col-span-7"
                        data-testid="water-history"
                    >
                        {phSeries.length >= 2 && (
                            <div className="card p-5 mb-5">
                                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--lime)] mb-2">
                                    {t.chart}
                                </div>
                                <Sparkline data={phSeries} />
                            </div>
                        )}

                        <h2 className="font-display font-bold text-[var(--cyan)] uppercase text-sm tracking-widest mb-4 flex items-center justify-between">
                            <span>{t.history}</span>
                            {readings.length > 0 && (
                                <a
                                    href={myWaterCsvUrl()}
                                    data-testid="water-csv-mine"
                                    className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--lime)] hover:text-[var(--cyan)] inline-flex items-center gap-1 normal-case"
                                    rel="noreferrer"
                                >
                                    <FileDown size={12} /> CSV
                                </a>
                            )}
                        </h2>
                        {!loaded ? null : readings.length === 0 ? (
                            <p className="font-mono text-[var(--muted)]">{t.none}</p>
                        ) : (
                            <div className="space-y-4" data-testid="water-list">
                                {readings.map((r) => (
                                    <article
                                        key={r.id}
                                        className="card p-5"
                                        data-testid={`reading-${r.id}`}
                                    >
                                        <div className="flex items-start justify-between flex-wrap gap-2">
                                            <div className="flex flex-wrap gap-2">
                                                {FIELDS.map((f) =>
                                                    r[f.key] != null ? (
                                                        <span
                                                            key={f.key}
                                                            className="font-mono text-xs border border-[var(--border)] px-2 py-1"
                                                        >
                                                            <span
                                                                className="text-[var(--muted)]"
                                                                style={{ color: f.color }}
                                                            >
                                                                {f.label}
                                                            </span>{" "}
                                                            <span className="text-white">
                                                                {r[f.key]}
                                                            </span>
                                                        </span>
                                                    ) : null,
                                                )}
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <span className="font-mono text-[10px] text-[var(--muted)]">
                                                    {new Date(r.created_at).toLocaleString()}
                                                </span>
                                                <button
                                                    onClick={() => remove(r.id)}
                                                    data-testid={`delete-reading-${r.id}`}
                                                    className="text-[var(--muted)] hover:text-[var(--coral)]"
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        </div>
                                        {(r.flags || []).length > 0 && (
                                            <div className="flex items-center gap-2 mt-3">
                                                <AlertTriangle
                                                    size={12}
                                                    className="text-[var(--coral)]"
                                                />
                                                <span className="font-mono text-[10px] uppercase tracking-widest text-[var(--coral)]">
                                                    {t.flagsLabel}
                                                </span>
                                                {r.flags.map((f) => (
                                                    <span
                                                        key={f}
                                                        className="font-mono text-[10px] border border-[var(--coral)] text-[var(--coral)] px-2 py-0.5"
                                                    >
                                                        {t[f] || f}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                        {r.notes && (
                                            <p className="font-mono text-xs text-[var(--muted)] mt-3 italic">
                                                "{r.notes}"
                                            </p>
                                        )}
                                        {r.diagnosis && (
                                            <div className="bg-black border border-[var(--lime)]/40 p-3 mt-3 font-mono text-sm text-[var(--lime)] whitespace-pre-wrap leading-relaxed">
                                                <div className="font-mono text-[10px] uppercase tracking-widest mb-1 text-[var(--lime)]">
                                                    FLOW //
                                                </div>
                                                {r.diagnosis}
                                            </div>
                                        )}
                                    </article>
                                ))}
                            </div>
                        )}
                    </section>
                </div>
            </div>
        </main>
    );
}

function Sparkline({ data }) {
    if (!data || data.length < 2) return null;
    const w = 600;
    const h = 100;
    const pad = 10;
    const ys = data.map((d) => d.y);
    const yMin = Math.min(...ys, 5.5);
    const yMax = Math.max(...ys, 8);
    const xStep = (w - pad * 2) / (data.length - 1);
    const pts = data.map((d, i) => {
        const x = pad + i * xStep;
        const y = pad + (1 - (d.y - yMin) / (yMax - yMin || 1)) * (h - pad * 2);
        return [x, y];
    });
    const path = pts.map((p, i) => `${i === 0 ? "M" : "L"}${p[0]},${p[1]}`).join(" ");
    // Ideal band 6.8-7.2
    const yBandTop = pad + (1 - (7.2 - yMin) / (yMax - yMin || 1)) * (h - pad * 2);
    const yBandBot = pad + (1 - (6.8 - yMin) / (yMax - yMin || 1)) * (h - pad * 2);
    return (
        <svg
            viewBox={`0 0 ${w} ${h}`}
            className="w-full h-24"
            data-testid="ph-sparkline"
            preserveAspectRatio="none"
        >
            <rect
                x={pad}
                y={Math.min(yBandTop, yBandBot)}
                width={w - pad * 2}
                height={Math.abs(yBandBot - yBandTop)}
                fill="rgba(212,255,0,0.08)"
            />
            <path d={path} fill="none" stroke="var(--cyan)" strokeWidth="2" />
            {pts.map(([x, y], i) => (
                <circle key={i} cx={x} cy={y} r="3" fill="var(--cyan)" />
            ))}
            <text
                x={pad}
                y={h - 2}
                fontSize="9"
                fill="var(--muted)"
                fontFamily="monospace"
            >
                {yMin.toFixed(1)}
            </text>
            <text
                x={pad}
                y={pad + 4}
                fontSize="9"
                fill="var(--muted)"
                fontFamily="monospace"
            >
                {yMax.toFixed(1)}
            </text>
        </svg>
    );
}
